import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PgmCueKind } from '../services/api';

export type PodcastLearningsEvidenceFilter =
  | ''
  | 'transcript'
  | 'frame'
  | 'both'
  | 'none';

export type PodcastLearningsIncludeFilter = '' | 'yes' | 'no';

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;

export interface PodcastLearningsFilterState {
  tab: number;
  podcastId: string;
  flow: string;
  failureMode: string;
  outcome: string;
  dateFrom: string;
  dateTo: string;
  evidenceFilter: PodcastLearningsEvidenceFilter;
  includeFilter: PodcastLearningsIncludeFilter;
  pgmCue: PgmCueKind;
  rowsPerPage: number;
  setTab: (tab: number) => void;
  setPodcastId: (podcastId: string) => void;
  setFlow: (flow: string) => void;
  setFailureMode: (failureMode: string) => void;
  setOutcome: (outcome: string) => void;
  setDateFrom: (dateFrom: string) => void;
  setDateTo: (dateTo: string) => void;
  setEvidenceFilter: (evidenceFilter: PodcastLearningsEvidenceFilter) => void;
  setIncludeFilter: (includeFilter: PodcastLearningsIncludeFilter) => void;
  setPgmCue: (pgmCue: PgmCueKind) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  resetFilters: () => void;
}

const DEFAULTS = {
  tab: 0,
  podcastId: '',
  flow: '',
  failureMode: '',
  outcome: '',
  dateFrom: '',
  dateTo: '',
  evidenceFilter: '' as PodcastLearningsEvidenceFilter,
  includeFilter: '' as PodcastLearningsIncludeFilter,
  pgmCue: 'curtain' as PgmCueKind,
  rowsPerPage: PAGE_SIZE_OPTIONS[0],
};

function normalizeRowsPerPage(value: unknown): number {
  const n = typeof value === 'number' ? value : PAGE_SIZE_OPTIONS[0];
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(n) ? n : PAGE_SIZE_OPTIONS[0];
}

function normalizePgmCue(value: unknown): PgmCueKind {
  if (value === 'banner' || value === 'lateral_banner' || value === 'curtain') return value;
  return 'curtain';
}

export const usePodcastLearningsStore = create<PodcastLearningsFilterState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setTab: (tab) => set({ tab }),
      setPodcastId: (podcastId) => set({ podcastId }),
      setFlow: (flow) => set({ flow }),
      setFailureMode: (failureMode) => set({ failureMode }),
      setOutcome: (outcome) => set({ outcome }),
      setDateFrom: (dateFrom) => set({ dateFrom }),
      setDateTo: (dateTo) => set({ dateTo }),
      setEvidenceFilter: (evidenceFilter) => set({ evidenceFilter }),
      setIncludeFilter: (includeFilter) => set({ includeFilter }),
      setPgmCue: (pgmCue) => set({ pgmCue }),
      setRowsPerPage: (rowsPerPage) => set({ rowsPerPage: normalizeRowsPerPage(rowsPerPage) }),
      resetFilters: () => set({ ...DEFAULTS }),
    }),
    {
      name: 'podcast-learnings-filters',
      version: 2,
      migrate: (persistedState) => {
        const s = (persistedState ?? {}) as Partial<PodcastLearningsFilterState>;
        return {
          ...DEFAULTS,
          ...s,
          rowsPerPage: normalizeRowsPerPage(s.rowsPerPage),
          pgmCue: normalizePgmCue(s.pgmCue),
        };
      },
      partialize: (state) => ({
        tab: state.tab,
        podcastId: state.podcastId,
        flow: state.flow,
        failureMode: state.failureMode,
        outcome: state.outcome,
        dateFrom: state.dateFrom,
        dateTo: state.dateTo,
        evidenceFilter: state.evidenceFilter,
        includeFilter: state.includeFilter,
        pgmCue: state.pgmCue,
        rowsPerPage: state.rowsPerPage,
      }),
    },
  ),
);

export { PAGE_SIZE_OPTIONS as PODCAST_LEARNINGS_PAGE_SIZE_OPTIONS };
